/**
 * /execution/holdbacks — split-pane: holdbacks ledger + dispute events.
 * Both are append-only canonical sinks. Read-only.
 *
 * Lifecycle honesty pass: the holdback panel now folds the ledger
 * into per-holdback state (Active / Paused / Resolved) and surfaces
 * anomaly tags (reversed, partially released, long-paused). The
 * dispute panel is left as-is — its events are intrinsically
 * chronological and don't fold into a single lifecycle per row.
 */
import { useMemo } from "react";
import { format } from "date-fns";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { LifeBuoy } from "lucide-react";
import { openResolutionDrawer } from "@/data/execution/resolutionDrawerBus";
import { UnifiedResolutionDrawer } from "@/components/execution/UnifiedResolutionDrawer";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { canAccessExecutionDomain } from "@/data/executionDomain";
import { deriveAllHoldbackStates } from "@/data/contractor/networkExecution/holdback/deriveState";
import {
  classifyHoldbackLifecycle,
  classifyHoldbackAnomaly,
  HOLDBACK_BUCKET_ORDER,
  type HoldbackLifecycleBucket,
} from "@/data/execution/lifecycleClassifiers";

const BUCKET_HEADER: Record<HoldbackLifecycleBucket, string> = {
  active: "Active holds",
  paused: "Paused — dispute open",
  resolved: "Resolved — released or reversed",
};

const ANOMALY_TONE_CLASS: Record<"warning" | "destructive" | "muted", string> = {
  warning: "border-warning text-warning",
  destructive: "border-destructive text-destructive",
  muted: "border-muted-foreground text-muted-foreground",
};

export default function ExecutionHoldbacksPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { role } = useRole();
  const { contractors, holdbackLedger, disputeEvents } = useAppData();

  const holdbackStates = useMemo(() => {
    return deriveAllHoldbackStates(holdbackLedger)
      .map((s) => ({
        state: s,
        lifecycle: classifyHoldbackLifecycle(s),
        anomaly: classifyHoldbackAnomaly(s),
      }))
      .sort((a, b) => +new Date(b.state.lastEntryAt) - +new Date(a.state.lastEntryAt))
      .slice(0, 150);
  }, [holdbackLedger]);

  const groupedHoldbacks = useMemo(() => {
    const m = new Map<HoldbackLifecycleBucket, typeof holdbackStates>();
    HOLDBACK_BUCKET_ORDER.forEach((b) => m.set(b, []));
    holdbackStates.forEach((row) => m.get(row.lifecycle.bucket)!.push(row));
    return m;
  }, [holdbackStates]);

  const disputes = useMemo(
    () => [...disputeEvents].sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt)).slice(0, 100),
    [disputeEvents],
  );

  const cName = useMemo(() => {
    const m = new Map<string, string>();
    contractors.forEach((c) => m.set(c.id, c.companyName));
    return m;
  }, [contractors]);

  if (!canAccessExecutionDomain(role)) return null;

  const content = (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[12px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Holdbacks</h2>
          <Badge variant="outline" className="text-[10px]">{holdbackStates.length} shown</Badge>
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">
          Grouped by derived state. Anomaly tags surface admin reversals, partial releases, and disputes that have been paused for more than two weeks.
        </p>
        {holdbackStates.length === 0 ? (
          <Card className="p-6 text-center text-sm text-muted-foreground">No holdback activity recorded.</Card>
        ) : (
          <div className="space-y-3">
            {HOLDBACK_BUCKET_ORDER.map((bucket) => {
              const rows = groupedHoldbacks.get(bucket)!;
              if (rows.length === 0) return null;
              return (
                <div key={bucket}>
                  <div className="sticky top-0 z-10 mb-1.5 flex items-center justify-between bg-background/95 backdrop-blur py-1">
                    <h3 className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                      {BUCKET_HEADER[bucket]}
                    </h3>
                    <Badge variant="outline" className="text-[10px]">{rows.length}</Badge>
                  </div>
                  <Card className="p-0 overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Last activity</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Job</TableHead>
                          <TableHead>Contractor</TableHead>
                          <TableHead className="text-right">Net held</TableHead>
                          <TableHead>Why flagged</TableHead>
                          <TableHead className="text-right"> </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.map(({ state, lifecycle, anomaly }) => (
                          <TableRow key={state.holdbackId}>
                            <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">
                              {format(new Date(state.lastEntryAt), "MMM d, h:mm a")}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px]" title={lifecycle.detail}>
                                {lifecycle.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-[11px]">{state.jobId}</TableCell>
                            <TableCell className="text-[11px]">{cName.get(state.contractorId) ?? state.contractorId}</TableCell>
                            <TableCell className="text-[11px] text-right font-mono">
                              ${state.netHeld.toLocaleString()}
                            </TableCell>
                            <TableCell>
                              {anomaly ? (
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] ${ANOMALY_TONE_CLASS[anomaly.tone]}`}
                                  title={anomaly.detail}
                                >
                                  {anomaly.label}
                                </Badge>
                              ) : (
                                <span className="text-[11px] text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`holdback-triage-${state.holdbackId}`}
                                onClick={() =>
                                  openResolutionDrawer({
                                    jobId: state.jobId,
                                    source: "holdback_row",
                                    eventContext: {
                                      kind: "holdback_state",
                                      at: state.lastEntryAt,
                                      headline: `Holdback ${lifecycle.label}`,
                                      detail: anomaly?.detail,
                                    },
                                  })
                                }
                              >
                                <LifeBuoy className="h-3 w-3" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Card>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[12px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Dispute Events</h2>
          <Badge variant="outline" className="text-[10px]">{disputes.length} shown</Badge>
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">
          Append-only event stream — newest first. Each row is one event in a dispute's lifecycle (open, evidence, withdraw, resolve).
        </p>
        {disputes.length === 0 ? (
          <Card className="p-6 text-center text-sm text-muted-foreground">No dispute activity recorded.</Card>
        ) : (
          <Card className="p-0 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Contractor</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {disputes.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="text-[11px] text-muted-foreground whitespace-nowrap">
                      {format(new Date(d.createdAt), "MMM d, h:mm a")}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{d.type}</Badge>
                    </TableCell>
                    <TableCell className="font-mono text-[11px]">{d.jobId}</TableCell>
                    <TableCell className="text-[11px]">{cName.get(d.contractorId) ?? d.contractorId}</TableCell>
                    <TableCell className="text-[11px] max-w-[180px] truncate" title={d.reason ?? ""}>{d.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </div>
  );

  if (embedded) return content;

  return (
    <>
      <CockpitPage
        
        title="Holdbacks & Disputes"
        maxWidth="7xl"
      >
        {content}
      </CockpitPage>
      <UnifiedResolutionDrawer />
    </>
  );
}
